import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Compass, Calculator, ChevronRight, ShieldAlert, Search, LayoutGrid, Star,
  Bookmark, Settings, Home as HomeIcon, Building2, Target, Gauge, Link2,
  Activity, Waves, Ruler, Clock,
} from "lucide-react";
import { LABELS, SETUP_STATUS } from "@/lib/displayLabels";

const H = ({ children }) => (
  <h3 className="label-up text-[var(--rs-amber)] mt-2">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-[12.5px] text-[var(--rs-text-2)] leading-relaxed">{children}</p>
);
const Note = ({ children, testid }) => (
  <div data-testid={testid} className="card-rs px-3.5 py-2.5 text-[12px] font-semibold leading-snug"
    style={{ borderLeft: "3px solid var(--rs-amber)", background: "rgba(255,179,0,0.06)" }}>
    {children}
  </div>
);

function Step({ n, icon: Icon, title, sub, points, why, children }) {
  return (
    <section className="card-rs p-4" data-testid={`howto-step-${n}`}>
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full font-mono-rs text-[11px] font-bold shrink-0"
          style={{ background: "rgba(245,180,0,0.16)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.6)" }}>{n}</span>
        <Icon className="w-4 h-4 shrink-0" style={{ color: "var(--rs-amber)" }} />
        <div className="text-[14px] font-bold">{title}</div>
      </div>
      <div className="text-[11px] label-up text-[var(--rs-text-3)] mb-2">{sub}</div>
      {children}
      {points && (
        <ul className="text-[12px] text-[var(--rs-text-2)] leading-relaxed list-disc pl-5 mt-1.5 space-y-0.5">
          {points.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {why && (
        <div className="mt-2.5">
          <div className="text-[10px] label-up text-[var(--rs-text-3)] mb-1">Why it matters</div>
          <p className="text-[12px] text-[var(--rs-text-2)] leading-relaxed">{why}</p>
        </div>
      )}
    </section>
  );
}

export default function HowToUse() {
  const navigate = useNavigate();
  const goSetup = () => navigate({ pathname: "/home", hash: "engineering-setup" });

  return (
    <div className="flex flex-col gap-4 pb-4" data-testid="howto-page">
      {/* Header */}
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)] inline-flex items-center gap-1.5">
          <Compass className="w-3.5 h-3.5" /> Getting Started
        </div>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1">How to Use RigSentinel</h2>
        <p className="text-[13px] text-[var(--rs-text-2)] mt-1 font-semibold">The well at your fingertips.</p>
        <P>
          RigSentinel is designed to work the way you need it to. Need a calculation quickly? Go straight to the
          calculator and enter the required data manually. Working with a rig or well? Set up your engineering
          workspace so RigSentinel can organize your rig, well and operational information and provide context
          to supported tools.
        </P>
      </section>
      <Note testid="howto-optional-note">
        Engineering Setup is optional. You do not need to complete Engineering Setup to use RigSentinel's calculators.
      </Note>

      {/* Two ways to work */}
      <section className="card-rs p-4" data-testid="howto-quick-calc">
        <div className="flex items-center gap-2 mb-1"><Calculator className="w-4 h-4" style={{ color: "var(--rs-amber)" }} /><span className="text-[14px] font-bold">Quick Calculation</span></div>
        <P>
          Need an answer now? Go directly to the calculator you need, enter the required values, confirm the units
          and calculate. No Engineering Setup required. Use Search, Categories, Favorites or Recently Used to locate tools.
        </P>
        <button type="button" data-testid="howto-find-calculator" onClick={() => navigate("/categories")}
          className="btn-amber h-10 px-4 rounded-md text-xs label-up mt-3 inline-flex items-center gap-1.5">
          Find a Calculator <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </section>

      <section className="card-rs p-4" data-testid="howto-workspace">
        <div className="flex items-center gap-2 mb-1"><Ruler className="w-4 h-4" style={{ color: "var(--rs-amber)" }} /><span className="text-[14px] font-bold">Engineering Workspace</span></div>
        <P>Working with a rig or well? Engineering Setup creates an organized working environment for the rig, well and current operation.</P>
        <div className="font-mono-rs text-[11px] leading-6 my-2.5 text-[var(--rs-text-2)]">
          ① RIG DATA SHEET<br />&nbsp;&nbsp;↓<br />② WELL PROFILES<br />&nbsp;&nbsp;↓<br />③ {LABELS.opstate.toUpperCase()}<br />&nbsp;&nbsp;↓<br />④ {LABELS.engSession.toUpperCase()}
        </div>
        <P>
          Where supported, RigSentinel can use information already available in the engineering workspace to provide
          context to its tools. You remain responsible for confirming that the information being used is correct.
        </P>
        <button type="button" data-testid="howto-start-setup" onClick={goSetup}
          className="btn-amber h-10 px-4 rounded-md text-xs label-up mt-3 inline-flex items-center gap-1.5">
          Start Engineering Setup <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </section>

      {/* Four steps */}
      <Step n={1} icon={Building2} title="Rig Data Sheet" sub="Define the rig"
        points={["Rig identification and specifications", "Mud pumps and pump information", "Surface line volumes", "BOP equipment", "Choke and kill system", "Riser information where applicable"]}
        why="Rig information can be maintained in one location instead of repeatedly entered or searched for. Where supported by a RigSentinel tool, existing rig information can provide engineering context. Always confirm stored information reflects the current rig configuration before operational use.">
        <P>The Rig Data Sheet stores supported engineering information associated with the rig.</P>
      </Step>

      <Step n={2} icon={Target} title="Well Profiles" sub="Define the well"
        points={["Well identification and geometry", "Hole sections, casing and liners", "Drill string and BHA", "Mud and cement programs", "MD/TVD information"]}
        why="A well changes as drilling progresses. Well Profiles provide a structured location for keeping the engineering configuration organized and available to supported RigSentinel tools. Keep the Well Profile current as the well configuration changes.">
        <P>Well Profiles organize engineering information associated with the well.</P>
      </Step>

      <Step n={3} icon={Gauge} title={LABELS.opstate} sub="Define what is happening now"
        why="Different operations can require different engineering information. Rig Operations helps establish: what is the rig doing now?">
        <P>
          {LABELS.opstate} is the user-facing presentation of Operational States. The rig and well may remain the
          same while the operation changes. {LABELS.opstate} records the current operational state — current MD/TVD,
          current mud weight and hole section — and provides operational context.
        </P>
      </Step>

      <Step n={4} icon={Link2} title={LABELS.engSession} sub="Bring the engineering context together"
        points={["Project · Rig · Well", "BHA · Drill string", "Hole section · Current operation"]}
        why={`${LABELS.engSession} tells RigSentinel which available engineering configuration you are currently working with. It can provide context to supported tools and help associate engineering work with the appropriate rig and well.`}>
        <P>{LABELS.engSession} is the user-facing access point for the Engineering Session. Based on existing functionality, it may establish context such as:</P>
      </Step>
      <Note testid="howto-rigeng-note">{LABELS.engSession} does not prevent you from manually using a calculator.</Note>

      {/* Ready to work */}
      <section className="card-rs p-4 text-center" style={{ borderColor: "rgba(74,222,128,0.4)", borderLeft: "3px solid #4ADE80" }} data-testid="howto-ready">
        <div className="text-[14px] font-bold" style={{ color: "#4ADE80" }}>YOU'RE READY TO WORK</div>
        <P>
          Once your engineering environment has been established, return to the Dashboard and use the tools you need.
          Engineering Setup remains available whenever something changes — you do not need to repeat the entire setup
          every time you use RigSentinel.
        </P>
        <p className="text-[12px] text-[var(--rs-text-3)] mt-2">Need only a calculation? Skip Engineering Setup and go directly to the calculator.</p>
      </section>

      {/* Current Operations */}
      <section className="card-rs p-4" data-testid="howto-current-ops">
        <div className="flex items-center gap-2 mb-1"><Activity className="w-4 h-4" style={{ color: "#4ADE80" }} /><span className="text-[14px] font-bold">Know Where You Are</span></div>
        <P>
          The Current Operations Dashboard card provides a quick view of active operational information available to
          RigSentinel. Where existing data supports it, this may include: Rig • Well • Current Operation • Hole Section •
          MD • TVD • Mud Weight • BHA • Drill String. Only actual available information is displayed.
        </P>
      </section>

      {/* Surface / Subsea */}
      <section className="card-rs p-4" data-testid="howto-surface-subsea">
        <div className="flex items-center gap-2 mb-1"><Waves className="w-4 h-4" style={{ color: "#60A5FA" }} /><span className="text-[14px] font-bold">Surface or Subsea?</span></div>
        <P><b>Surface</b> — use for an applicable surface BOP/well-control configuration.</P>
        <P><b>Subsea</b> — use for an applicable subsea configuration where riser and choke/kill-line considerations may apply.</P>
        <Note>Confirm the correct Surface/Subsea selection before performing an applicable engineering or well-control calculation. Changing mode may change the information required by supported tools.</Note>
      </section>

      {/* Status meanings */}
      <section className="card-rs p-4" data-testid="howto-status">
        <div className="text-[14px] font-bold mb-2">Understanding Setup Status</div>
        {[
          ["not_started", "No required setup information entered."],
          ["in_progress", "Some information exists but setup remains incomplete."],
          ["ready", "Required information is available."],
          ["attention", "Previously configured information requires review."],
        ].map(([k, d]) => (
          <div key={k} className="flex items-center gap-2.5 py-1">
            <span className="label-up text-[8.5px] px-1.5 py-0.5 rounded shrink-0"
              style={{ background: SETUP_STATUS[k].bg, color: SETUP_STATUS[k].color, border: `1px solid ${SETUP_STATUS[k].border}` }}>
              {SETUP_STATUS[k].label}
            </span>
            <span className="text-[12px] text-[var(--rs-text-2)]">{d}</span>
          </div>
        ))}
        <Note testid="howto-ready-note">Ready means required information is present. It does not certify that the engineering data itself is correct.</Note>
      </section>

      {/* Find what you need */}
      <section className="card-rs p-4" data-testid="howto-find">
        <div className="text-[14px] font-bold mb-2">Find What You Need</div>
        {[[Search, "Search", "Search by calculator, tool or engineering function."],
          [LayoutGrid, "Categories", "Browse tools by engineering category."],
          [Star, "Favorites", "Keep frequently used tools close."],
          [Clock, "Recently Used", "Expand the Dashboard control to see up to the last 10 used tools, newest first."],
          [Bookmark, "Saved", "Return to supported saved calculations and worksheets."]].map(([Icon, t, d]) => (
          <div key={t} className="flex items-start gap-2.5 py-1.5">
            <Icon className="w-4 h-4 mt-0.5 shrink-0 text-[var(--rs-text-3)]" />
            <div><div className="text-[12.5px] font-semibold">{t}</div><div className="text-[11.5px] text-[var(--rs-text-3)]">{d}</div></div>
          </div>
        ))}
      </section>

      {/* Bottom navigation */}
      <section className="card-rs p-4" data-testid="howto-bottomnav">
        <div className="text-[14px] font-bold mb-2.5">Bottom Navigation</div>
        <div className="flex items-center justify-between rounded-md border px-4 py-2.5 mb-3" style={{ borderColor: "var(--rs-border)", background: "rgba(0,0,0,0.3)" }}>
          {[[HomeIcon, "HOME"], [LayoutGrid, "CATEGORIES"], [Star, "FAVORITES"], [Bookmark, "SAVED"], [Settings, "SETTINGS"]].map(([Icon, t]) => (
            <div key={t} className="flex flex-col items-center gap-1">
              <Icon className="w-4 h-4 text-[var(--rs-text-3)]" />
              <span className="text-[7.5px] label-up text-[var(--rs-text-3)]">{t}</span>
            </div>
          ))}
        </div>
        {[["HOME", "Return to the main Dashboard."],
          ["CATEGORIES", "Browse engineering calculators and tools by category."],
          ["FAVORITES", "Access frequently used tools saved as favorites."],
          ["SAVED", "Return to supported calculations, worksheets and engineering work previously saved. Verify that older saved inputs still represent current operating conditions."],
          ["SETTINGS", "Access available application/account settings and supporting information, including Engineering & Safety information and About RigSentinel."]].map(([t, d]) => (
          <div key={t} className="py-1"><span className="text-[11px] font-bold label-up">{t}</span><p className="text-[11.5px] text-[var(--rs-text-3)]">{d}</p></div>
        ))}
      </section>

      {/* Before you calculate */}
      <section className="card-rs p-4" style={{ borderLeft: "3px solid var(--rs-amber)" }} data-testid="howto-before-calc">
        <div className="text-[14px] font-bold mb-2">Before You Calculate</div>
        {[["1. Confirm the Configuration", "Confirm Surface/Subsea mode where applicable."],
          ["2. Confirm the Input Source", "Enter the required information manually or use available engineering context where supported."],
          ["3. Check the Numbers", "Confirm every critical input value and its associated unit."],
          ["4. Calculate", "Perform the calculation and review the result."],
          ["5. Verify", "Verify safety-critical results before operational use."]].map(([t, d]) => (
          <div key={t} className="py-1"><div className="text-[12.5px] font-semibold">{t}</div><div className="text-[11.5px] text-[var(--rs-text-3)]">{d}</div></div>
        ))}
        <Note testid="howto-decision-note">RigSentinel assists the calculation. You make the engineering decision.</Note>
      </section>

      {/* Units */}
      <section className="card-rs p-4" data-testid="howto-units">
        <div className="text-[14px] font-bold mb-1">Imperial &amp; Metric</div>
        <P>RigSentinel supports Imperial and Metric units across supported tools. Never assume a value is in the expected unit merely because the number looks familiar.</P>
        <Note>Check the value. Check the unit. Check the result.</Note>
      </section>

      {/* Saving */}
      <section className="card-rs p-4" data-testid="howto-saving">
        <div className="text-[14px] font-bold mb-1">Saving Your Work</div>
        <P>Where supported, important calculations and worksheets can be saved and retrieved. Before relying on previously saved calculations, confirm that the inputs still represent current well and operating conditions.</P>
      </section>

      {/* Responsibility */}
      <section className="card-rs p-4" data-testid="howto-responsibility">
        <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-4 h-4" style={{ color: "var(--rs-amber)" }} /><span className="text-[14px] font-bold">Engineering Responsibility</span></div>
        <P>RigSentinel is an engineering support tool designed to assist field personnel with calculations, engineering information and operational organization. It does not replace:</P>
        <ul className="text-[12px] text-[var(--rs-text-2)] list-disc pl-5 mt-1 space-y-0.5">
          <li>Approved company procedures</li><li>Drilling or well programs</li><li>Verified rig and well data</li>
          <li>Applicable regulations and standards</li><li>Required supervisory or engineering approvals</li><li>Competent engineering judgment</li>
        </ul>
        <Note>Always verify safety-critical inputs and results before operational use.</Note>
        <button type="button" data-testid="howto-full-disclaimer" onClick={() => navigate("/safety")}
          className="font-mono-rs text-[10px] tracking-[0.18em] uppercase mt-2.5 inline-flex items-center gap-1 font-semibold" style={{ color: "var(--rs-amber)" }}>
          Full Engineering &amp; Safety Disclaimer <ChevronRight className="w-3 h-3" />
        </button>
      </section>

      {/* Help */}
      <section className="card-rs p-4" data-testid="howto-help">
        <div className="text-[14px] font-bold mb-1">Need Help?</div>
        <P>This guide is always available from the Dashboard's How to Use RigSentinel card. The Engineering &amp; Safety Disclaimer and About RigSentinel are available in Settings.</P>
      </section>

      {/* Final */}
      <section className="card-rs p-5 text-center" style={{ borderColor: "rgba(245,180,0,0.5)" }} data-testid="howto-final">
        <div className="text-[15px] font-black">READY TO GO?</div>
        <p className="text-[12.5px] text-[var(--rs-text-2)] mt-1">Set up when it helps. Calculate when you need it. Verify before you use it.</p>
        <button type="button" data-testid="howto-return-dashboard" onClick={() => navigate("/home")}
          className="btn-amber h-10 px-5 rounded-md text-xs label-up mt-3">
          Return to Dashboard
        </button>
      </section>
    </div>
  );
}
